import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Alert } from "react-bootstrap";
import Footer from "../../components/Footer";
import "./Contact.css";
import PhoneInput from "react-phone-input-2";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import emailjs from "@emailjs/browser";

// Initialize EmailJS with your public key
emailjs.init("aEASMHR8n6Vmgtj3l");

const Contact = ({ brochureName }) => {
  const location = useLocation();

  // Scroll to top when component loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /*
   * Normalize URL path.
   *
   * This makes both:
   * /metafunction
   * /metafunction/
   *
   * resolve to:
   * /metafunction
   */
  const normalizedPath =
    location.pathname.replace(/\/+$/, "") || "/";

  /*
   * Brochure mapping
   */
  const pageBrochureMap = {
    "/metasurface2": "MetaSurface2",
    "/metasurface": "MetaSurface",
    "/metaparametric": "MetaParametric",
    "/metaform": "MetaForm",
    "/metafunction": "MetaFunction",
    "/ctb": "Coffee Table Book",
  };

  /*
   * Detect brochure from URL.
   *
   * Example:
   * /metafunction/
   * =>
   * MetaFunction
   */
  const detectedBrochure =
    pageBrochureMap[normalizedPath] ||
    brochureName ||
    "Unknown";

  /*
   * H1 / H2 copy (Yash task 5). Product name as H1, differentiator
   * as H2 — this is what makes each of the four brochure pages
   * genuinely different content instead of the same template with
   * the brand name swapped in.
   */
  const headingMap = {
    "/metasurface": {
      h1: "Browse Metaguise's facade finishes and coatings",
      h2: "Premium Finishes & Coating Systems by Metaguise",
    },
    "/metaparametric": {
      h1: "See how Metaguise turns algorithmic design into buildable metal facades",
      h2: "Algorithmic Facade Design by Metaguise",
    },
    "/metaform": {
      h1: "Discover Metaguise's sculptural metal facade systems",
      h2: "Sculptural Metal Facade Collection by Metaguise",
    },
    "/metafunction": {
      h1: "Explore Metaguise's performance-driven facade systems",
      h2: "Performance-Driven Metal Facade Systems by Metaguise",
    },
    "/ctb": {
      h1: "Explore Metaguise's metal facade products and projects ",
      h2: "Odyssey",
    },
  };

  const currentHeadings =
    headingMap[normalizedPath] || { h1: detectedBrochure, h2: "" };

  /*
   * Breadcrumb schema (JSON-LD) — only emitted when the current path
   * is one of the known brochure routes.
   */
  const breadcrumbSchema = pageBrochureMap[normalizedPath]
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://metaguise.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": pageBrochureMap[normalizedPath],
            "item": `https://metaguise.com${normalizedPath}/`
          }
        ]
      }
    : null;

  /*
   * Form data
   */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: `The user has requested the ${detectedBrochure} brochure.`,
  });

  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [showLeadSuccess, setShowLeadSuccess] = useState(false);

  /*
   * Handle normal input changes
   */
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  /*
   * Handle phone input
   */
  const handlePhoneChange = (value) => {
    setFormData({
      ...formData,
      phone: value,
    });
  };

  /*
   * Handle reCAPTCHA
   */
  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  /*
   * Phone placeholder
   */
  useEffect(() => {
    const inputField = document.querySelector(".form-text input");

    if (inputField) {
      if (!formData.phone || formData.phone.length <= 3) {
        inputField.setAttribute(
          "placeholder",
          "Enter your mobile number"
        );
      } else {
        inputField.setAttribute("placeholder", "");
      }
    }
  }, [formData.phone]);

  /*
   * Open brochure PDF
   */
  const openPDF = () => {
    const brochureMap = {
      MetaSurface: "/assets/brochure/METASURFACE.pdf",
      MetaSurface2: "/assets/brochure/METASURFACE.pdf",
      MetaParametric: "/assets/brochure/METAPARAMETRIC.pdf",
      MetaForm: "/assets/brochure/METAFORM.pdf",
      MetaFunction: "/assets/brochure/METAFUNCTION.pdf",
      "Coffee Table Book": "/assets/brochure/ctb.pdf",
    };

    const filePath = brochureMap[detectedBrochure];

    if (filePath) {
      window.open(filePath, "_blank");
    } else {
      console.error(
        "No brochure PDF found for:",
        detectedBrochure
      );
    }
  };

  /*
   * Get call source based on URL
   */
  const getCallSource = () => {
    const pathToCallSource = {
      "/metasurface": "METASURFACE",
      "/metasurface2": "METASURFACE2",
      "/metaparametric": "METAPARAMETRIC",
      "/metaform": "METAFORM",
      "/metafunction": "METAFUNCTION",
      "/ctb": "COFFEE TABLE BOOK",
      "/contact": "CONTACT",
      "/partner": "PARTNER",
      "/build": "BUILD",
    };

    return pathToCallSource[normalizedPath] || "CONTACT";
  };

  /*
   * Send email using EmailJS
   */
  const sendEmail = async () => {
    const templateParams = {
      to_name: "Metaguise Team",
      from_name: formData.name,
      from_email: formData.email,
      from_phone: formData.phone,

      phone: formData.phone,
      phone_number: formData.phone,
      mobile: formData.phone,

      brochure_name: detectedBrochure,

      message: formData.message,

      timestamp: new Date().toLocaleString(),

      subject: `New ${detectedBrochure} Brochure Download Request`,

      reply_to: formData.email,
    };

    console.log(
      "Sending email with params:",
      templateParams
    );

    try {
      const response = await emailjs.send(
        "service_hbh6e6a",
        "template_sp4d06m",
        templateParams
      );

      console.log(
        "EmailJS response success:",
        response
      );

      return {
        success: true,
        message: "Email sent successfully",
      };
    } catch (error) {
      console.error(
        "EmailJS error:",
        error
      );

      return {
        success: false,
        message:
          "Failed to send email notification",
      };
    }
  };

  /*
   * Create lead in backend
   */
  const createLead = async () => {
    const callSource = getCallSource();

    const currentDateTime =
      new Date().toISOString();

    /*
     * Lead assignment
     */
    const leadAssignments = [
      {
        role: "PRE_SALES",
        employeeId:
          "694bbefcf956d21d2f8f2f90",
        employeeName: "Kajal Arya",
        assignAt: currentDateTime,
      },
    ];

    /*
     * Lead payload
     */
    const payload = {
      firstName:
        formData.name.split(" ")[0] ||
        formData.name,

      fullName: formData.name,

      contact: formData.phone,

      email: formData.email,

      address: "null",

      locality: "null",

      city: "null",

      district: "null",

      state: "null",

      pincode: "000000",

      pincodeMappingId:
        "693f98b3f956d25cedd37dfc",

      projectType: "null",

      customerType: "null",

      engagementTimeline: "null",

      has3dOrSiteDrawings: false,

      approximateFacadeCladdingSqFt: 0,

      projectBrief: formData.message,

      productCategory: "null",

      productBrand: "Metaguise",

      productId:
        "69412167f956d233e1261afc",

      callStatus: "NEW_LEAD",

      remarks:
        `Requested ${detectedBrochure} brochure. ${formData.message}`,

      callRegistration: true,

      leadAssignments:
        leadAssignments,

      callSource: callSource,
    };

    console.log(
      "Creating lead with payload:",
      payload
    );

    console.log(
      "Lead assignments:",
      leadAssignments
    );

    console.log(
      "callSource value:",
      callSource
    );

    try {
      const response = await fetch(
        "https://backend.cshare.in/api/customer/create",
        {
          method: "POST",

          headers: {
            companyId:
              "693f9759f956d25cedd37a6f",

            apikey:
              "918ef419818745ef1f09f705a9642545",

            "Content-Type":
              "application/json",
          },

          body: JSON.stringify(payload),
        }
      );

      const responseText =
        await response.text();

      console.log(
        "Lead creation response:",
        response.status,
        responseText
      );

      if (response.ok) {
        setShowLeadSuccess(true);

        return {
          success: true,
          message:
            "Lead created successfully",
        };
      }

      console.error(
        "Failed to create lead:",
        responseText
      );

      return {
        success: false,
        message:
          "Failed to create lead in backend",
      };
    } catch (err) {
      console.error(
        "Error creating lead:",
        err
      );

      return {
        success: false,
        message:
          "Network error creating lead",
      };
    }
  };

  /*
   * Submit form
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSending(true);
    setFeedbackMessage("");
    setShowLeadSuccess(false);

    /*
     * Required field validation
     */
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.phone.trim()
    ) {
      setFeedbackMessage(
        "❌ All fields are required."
      );

      setIsSending(false);
      return;
    }

    /*
     * Email validation
     */
    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (
      !emailRegex.test(formData.email)
    ) {
      setFeedbackMessage(
        "❌ Please enter a valid email address."
      );

      setIsSending(false);
      return;
    }

    /*
     * Phone validation
     */
    if (
      formData.phone.replace(/\D/g, "")
        .length < 10
    ) {
      setFeedbackMessage(
        "❌ Please enter a valid phone number."
      );

      setIsSending(false);
      return;
    }

    /*
     * CAPTCHA validation
     */
    if (!captchaValue) {
      setFeedbackMessage(
        "⚠️ Please verify the reCAPTCHA."
      );

      setIsSending(false);
      return;
    }

    try {
      /*
       * Step 1:
       * Open brochure
       */
      openPDF();

      /*
       * Step 2:
       * Create backend lead
       */
      const leadResult =
        await createLead();

      /*
       * Step 3:
       * Send email
       */
      const emailResult =
        await sendEmail();

      /*
       * Step 4:
       * Feedback
       */
      if (
        leadResult.success &&
        emailResult.success
      ) {
        setFeedbackMessage(
          `✅ Thanks for your query! Your ${detectedBrochure} Brochure Download is ready!`
        );
      } else if (
        leadResult.success &&
        !emailResult.success
      ) {
        setFeedbackMessage(
          "✅ Thanks for your query! Brochure downloaded and lead created. Email notification failed."
        );
      } else if (
        !leadResult.success &&
        emailResult.success
      ) {
        setFeedbackMessage(
          "✅ Thanks for your query! Brochure downloaded and email sent. Lead creation failed."
        );
      } else {
        setFeedbackMessage(
          `✅ Thanks for your query! Your ${detectedBrochure} Brochure downloaded. Request processing had issues.`
        );
      }

      /*
       * Step 5:
       * Reset form
       */
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: `The user has requested the ${detectedBrochure} brochure.`,
      });

      setCaptchaValue(null);
    } catch (error) {
      console.error(
        "Error in form submission:",
        error
      );

      setFeedbackMessage(
        "❌ Something went wrong. Please try again."
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Download {detectedBrochure} Brochure |
          Luxury Metal Facades & Cladding
        </title>

        <link
          rel="canonical"
          href={`https://metaguise.com${normalizedPath}/`}
        />

        {breadcrumbSchema && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        )}

        <meta
          property="og:url"
          content={`https://metaguise.com${normalizedPath}/`}
        />

        <meta
          name="description"
          content={`Explore our premium ${detectedBrochure} designs. Download the brochure for innovative architectural surfaces.`}
        />

        <meta
          property="og:title"
          content={`Download ${detectedBrochure} Brochure | Luxury Metal Facades & Cladding`}
        />

        <meta
          property="og:description"
          content={`Explore our premium ${detectedBrochure} designs. Download the brochure for innovative architectural surfaces.`}
        />
      </Helmet>

      <Container
        fluid
        className="bg-dark text-white contact-container"
      >
        <Row className="contact-row">

          {/* LEFT SIDE */}
          <Col
            md={6}
            className="contact-left d-flex flex-column justify-content-center gap-4"
          >
            <h1
              style={{
                position: "absolute",
                width: "1px",
                height: "1px",
                padding: 0,
                margin: "-1px",
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              {currentHeadings.h1}
            </h1>

            {currentHeadings.h2 && (
              <h2
                style={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  padding: 0,
                  margin: "-1px",
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }}
              >
                {currentHeadings.h2}
              </h2>
            )}

            <div className="contactus1-text">
              <p>Thank you for</p>

              <p>showing interest in</p>

              <p>
                {detectedBrochure} brochure!
              </p>
            </div>

            <div className="lead-contact">
              <p>
                Please fill the form to
                download it.
              </p>
            </div>
          </Col>

          {/* RIGHT SIDE */}
          <Col
            md={6}
            className="contact-right d-flex flex-column justify-content-center"
          >
            <Form
              className="w-100"
              onSubmit={handleSubmit}
            >

              {/* NAME + EMAIL */}
              <Row>

                <Col
                  md={6}
                  className="mb-3 mb-md-4"
                >
                  <Form.Group
                    controlId="formName"
                  >
                    <Form.Control
                      type="text"
                      name="name"
                      placeholder="Name *"
                      className="bg-contact form-text border-0"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={isSending}
                    />
                  </Form.Group>
                </Col>

                <Col
                  md={6}
                  className="mb-3 mb-md-4"
                >
                  <Form.Group
                    controlId="formEmail"
                  >
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Email *"
                      className="bg-contact form-text border-0"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSending}
                    />
                  </Form.Group>
                </Col>

              </Row>

              {/* PHONE */}
              <Row>

                <Col
                  md={12}
                  className="mb-3 mb-md-4"
                >
                  <Form.Group
                    controlId="formPhone"
                  >
                    <PhoneInput
                      enableSearch
                      inputClass="bg-contact form-text border-0 w-100"
                      containerClass="w-100"
                      inputStyle={{
                        width: "100%",
                      }}
                      placeholder="Phone Number *"
                      dropdownClass="bg-dark text-white"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      required
                      disabled={isSending}
                    />
                  </Form.Group>
                </Col>

              </Row>

              {/* MESSAGE */}
              <Row>

                <Col
                  md={12}
                  className="mb-3 mb-md-4"
                >
                  <Form.Group
                    controlId="formMessage"
                  >
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="message"
                      placeholder="Message *"
                      className="bg-contact form-text border-0"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={isSending}
                    />
                  </Form.Group>
                </Col>

              </Row>

              {/* RECAPTCHA */}
              <Row>

                <Col
                  md={12}
                  className="mb-3 mb-md-4"
                >
                  <div className="d-flex justify-content-center">

                    <ReCAPTCHA
                      sitekey="6Lf5GwksAAAAAILPCzd0RMkNRtjFLPyph-uV56Ev"
                      onChange={handleCaptchaChange}
                      theme="dark"
                      disabled={isSending}
                    />

                  </div>
                </Col>

              </Row>

              {/* SUBMIT BUTTON */}
              <div className="button-wrapper">

                <button
                  type="submit"
                  className="send-button"
                  disabled={isSending}
                >
                  <span>
                    {isSending
                      ? "Processing..."
                      : `Send & View ${detectedBrochure} Brochure`}
                  </span>
                </button>

              </div>

              {/* FEEDBACK */}
              {feedbackMessage && (
                <Alert
                  variant={
                    feedbackMessage.includes(
                      "❌"
                    ) ||
                    feedbackMessage.includes(
                      "⚠️"
                    )
                      ? "danger"
                      : "success"
                  }
                  className="mt-3 text-center"
                >
                  {feedbackMessage}
                </Alert>
              )}

            </Form>
          </Col>

        </Row>
      </Container>

      <Footer />
    </>
  );
};

export default Contact;